import { FC, useState } from "react"

import {
  Box,
  ButtonProps,
  Stack,
  TextField,
  Typography,
  TypographyProps,
} from "@mui/material"
import Dialog from "components/common/dialog/dialog"
import { Trans, useTranslation } from "react-i18next"

const DELETE_CONFIRMATION_WORD = "Delete"

interface IProps {
  titleText: string
  descriptionText?: string
  descriptionTextProps?: TypographyProps
  isOpen: boolean
  onClose: (event: () => void, reason: string) => void
  onActionButtonClick: () => void
  actionAcceptText: string
  actionAcceptButtonProps?: ButtonProps<"button">
  isLoading?: boolean
  disableEscapeKeyDown?: boolean
  children?: React.ReactNode
  requireDeleteConfirmation?: boolean
}

const DialogRemoveStudent: FC<IProps> = ({
  titleText,
  descriptionText,
  descriptionTextProps,
  onActionButtonClick,
  onClose,
  isOpen,
  isLoading,
  children,
  actionAcceptText,
  actionAcceptButtonProps,
  disableEscapeKeyDown,
  requireDeleteConfirmation = false,
}) => {
  const { t } = useTranslation()
  const [deleteConfirmation, setDeleteConfirmation] = useState("")

  const isConfirmed = deleteConfirmation === DELETE_CONFIRMATION_WORD

  const handleClose: typeof onClose = (event, reason) => {
    setDeleteConfirmation("")
    onClose(event, reason)
  }

  const mergedAcceptButtonProps: ButtonProps<"button"> = {
    ...actionAcceptButtonProps,
    ...(requireDeleteConfirmation && {
      disabled: !isConfirmed || actionAcceptButtonProps?.disabled,
    }),
  }

  const handleConfirmViaEnter = () => {
    if (isConfirmed) {
      onActionButtonClick()
    }
  }

  return (
    <Dialog
      titleText={titleText}
      descriptionText={descriptionText}
      descriptionTextProps={descriptionTextProps}
      open={isOpen}
      onClose={handleClose}
      onActionButtonClick={onActionButtonClick}
      actionAcceptText={actionAcceptText}
      actionAcceptButtonProps={mergedAcceptButtonProps}
      isLoading={isLoading}
      disableEscapeKeyDown={disableEscapeKeyDown}
    >
      {children}
      {requireDeleteConfirmation && (
        <Stack gap={0.5}>
          <Typography
            variant="body1"
            fontWeight={500}
            color="mockup.neutral40"
          >
            <Trans
              i18nKey="students.typeDeleteToConfirm"
              components={{
                bold: (
                  <Box component="span" sx={{ fontWeight: 700 }} />
                ),
              }}
            />
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={t("students.deleteConfirmationPlaceholder")}
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleConfirmViaEnter()
              }
            }}
          />
        </Stack>
      )}
    </Dialog>
  )
}

export default DialogRemoveStudent
